/**
 * Gələnlər qutusunun nişanları — İlkin / Sosial / Tanıtımlar / Güncəlliklər.
 *
 * NİYƏ LAZIMDIR
 * KOB-un qutusunda müştəri yazışması bülletenlər arasında itir. Rəqiblərin
 * hamısında bu ayrım var və müştəri bizə keçəndə onun yoxluğunu dərhal
 * hiss edir.
 *
 * ⚠️ MƏKTUB KÖÇÜRÜLMÜR — BU, SÜZGƏCDİR.
 * Hər şey INBOX-da qalır; nişan arxa tərəfdə başlıqlardan hesablanır
 * (panel-api/src/mail/category.ts). Outlook və telefonda qutu olduğu kimi
 * görünür — orada dörd yeni qovluq peyda olmur.
 *
 * ⚠️ SAY HƏR NİŞANIN ÜSTÜNDƏ YAZILIR — VƏ BU, TƏSADÜFİ DEYİL.
 * Nişanların ən böyük təhlükəsi məktubun GÖRÜNMƏZ olmasıdır: müştəri
 * fakturasını gözləyir, o isə «Tanıtımlar»a düşür və heç kim baxmır.
 * Say göstərilməsə, boş «İlkin» ekranı «poçt gəlmir» kimi oxunur.
 * Rəqəm isə «orada 12 məktub var» deyir və istifadəçi bir klik uzaqdadır.
 */

import type { MailCategory } from '../types';

interface Props {
  active: MailCategory | null;
  onSelect: (c: MailCategory | null) => void;
  /** Nişanlara görə say — taranmış hissədə. */
  counts: Record<MailCategory, number> | null;
  /** Neçə məktub tarandı / qutuda cəmi neçədir. */
  scanned: number;
  total: number;
  busy: boolean;
}

/**
 * Nişanların adı və izahı.
 *
 * ⚠️ İZAH `title` KİMİ VERİLİR. «Güncəlliklər» sözü tək başına heç nə
 * demir; istifadəçi hansı məktubun ora düşdüyünü bilməlidir, yoxsa
 * nişanlara ümumiyyətlə baxmaz.
 */
const TABS: { key: MailCategory | null; label: string; hint: string }[] = [
  {
    key: null,
    label: 'Hamısı',
    hint: 'Bütün gələn məktublar — nişandan asılı olmayaraq',
  },
  {
    key: 'primary',
    label: 'İlkin',
    hint: 'İnsanların yazdığı məktublar və yazışmaların davamı',
  },
  {
    key: 'social',
    label: 'Sosial',
    hint: 'LinkedIn, Facebook, Instagram və digər sosial şəbəkələr',
  },
  {
    key: 'promotions',
    label: 'Tanıtımlar',
    hint: 'Bülletenlər və reklam — abunəlikdən çıxma bağlantısı olanlar',
  },
  {
    key: 'updates',
    label: 'Güncəlliklər',
    hint: 'Avtomatik bildirişlər: bank, sifariş, sistem məktubları',
  },
];

export function CategoryTabs({
  active,
  onSelect,
  counts,
  scanned,
  total,
  busy,
}: Props): React.ReactElement {
  // ⚠️ TARAMA MƏHDUDDUR VƏ BU, GİZLƏDİLMİR.
  // Arxa tərəf ən çoxu 600 məktub oxuyur. Qutuda daha çoxu varsa, saylar
  // TAM DEYİL — bunu deməsək, «Tanıtımlar 40» yazısını görən adam
  // qutusunda cəmi 40 reklam olduğunu düşünər.
  const partial = counts !== null && scanned < total;

  return (
    <div className="cat-tabs" role="tablist" aria-label="Gələnlər nişanları">
      {TABS.map((t) => {
        const n = t.key === null ? null : (counts?.[t.key] ?? null);
        const isOn = active === t.key;
        return (
          <button
            key={t.key ?? 'all'}
            role="tab"
            aria-selected={isOn}
            className={`cat-tab${isOn ? ' is-on' : ''}`}
            title={t.hint}
            disabled={busy}
            onClick={() => onSelect(t.key)}
          >
            <span className="cat-tab-label">{t.label}</span>
            {/*
              ⚠️ SIFIR DA GÖSTƏRİLİR, GİZLƏDİLMİR.
              Boş nişanı saysız göstərsək, istifadəçi onu «hələ
              yüklənməyib» sanıb gözləyər. «0» isə qəti cavabdır.
            */}
            {n !== null && (
              <span className="cat-tab-count" aria-hidden="true">
                {n}
              </span>
            )}
            {n !== null && <span className="sr-only">{`${n} məktub`}</span>}
          </button>
        );
      })}

      {partial && (
        <span className="cat-tabs-note" title={`Qutuda ${total} məktub var`}>
          son {scanned} məktub üzrə
        </span>
      )}
    </div>
  );
}
